import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaWhatsapp } from 'react-icons/fa';

const WhatsAppFloatingButton: React.FC = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // If we scroll down past 100px, hide the button
      // If we scroll up, show the button
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Auto-open the text after a few seconds when the site loads
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsHovered(true);

      // Close it back after 3 seconds
      const closeTimer = setTimeout(() => {
        setIsHovered(false);
      }, 3000);

      return () => clearTimeout(closeTimer);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} // Smooth easing
          className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 flex items-center"
        >
          <a
            href="https://wa.me/264813236891"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-end relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            aria-label="Chat with us on WhatsApp"
          >
            {/* Slide-out container */}
            <motion.div
              initial={false}
              animate={{
                width: isHovered ? 'auto' : 0,
                opacity: isHovered ? 1 : 0,
                // Adjusting padding so it slides seamlessly from behind
                paddingRight: isHovered ? '3.5rem' : 0,
              }}
              transition={{
                duration: 0.4,
                ease: [0.2, 0.8, 0.2, 1], // Smooth, snappy spring-like easing
              }}
              // Using amber-700 (our brand brown) and fully rounded matching the circle
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-amber-700 text-white rounded-full py-2.5 md:py-3 shadow-lg overflow-hidden whitespace-nowrap z-0 border border-amber-600/50 flex items-center"
              style={{
                paddingLeft: isHovered ? '1.5rem' : '0rem',
                minWidth: isHovered ? 'max-content' : '0px',
              }}
            >
              <span className="font-semibold text-sm md:text-base mr-2 md:mr-4">Book a tour</span>
            </motion.div>

            {/* Main WhatsApp Button */}
            <motion.div
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 17 }}
              // Responsive sizing: W/H 50px on mobile, 60px on tablet, 70px on desktop
              className="relative w-[50px] h-[50px] md:w-[60px] md:h-[60px] lg:w-[70px] lg:h-[70px] rounded-full flex items-center justify-center shadow-[0_4px_14px_0_rgba(37,211,102,0.39)] z-10"
              style={{ backgroundColor: '#25D366' }} // Official WhatsApp Green
            >
              <FaWhatsapp className="text-white text-2xl md:text-3xl lg:text-4xl" />

              {/* Subtle ping animation to draw attention */}
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-75 animate-ping" style={{ animationDuration: '3s' }}></span>
            </motion.div>
          </a>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WhatsAppFloatingButton;
